import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ArrowDownCircle, RefreshCcw, HeartHandshake, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface WalletTosGateProps {
  parentId: string | null;
  children: React.ReactNode;
}

type TosStatus =
  | { phase: 'loading' }
  | { phase: 'accepted' }
  | { phase: 'pending'; accountId: string }
  | { phase: 'no_account' };

/**
 * Guards any wallet action behind BHCS Cash Terms of Service acceptance.
 *
 * - Accepted: renders children immediately.
 * - Not accepted: renders an inline ToS gate (summary + "I Agree" button).
 * - No balance account / null parentId: renders nothing (wallet UI is
 *   already hidden in those cases).
 */
export function WalletTosGate({ parentId, children }: WalletTosGateProps) {
  const [status, setStatus] = useState<TosStatus>({ phase: 'loading' });
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!parentId) {
      setStatus({ phase: 'no_account' });
      return;
    }
    setStatus({ phase: 'loading' });
    (async () => {
      const { data } = await supabase
        .from('balance_accounts')
        .select('id, wallet_tos_accepted_at')
        .eq('parent_id', parentId)
        .maybeSingle() as { data: { id: string; wallet_tos_accepted_at: string | null } | null };

      if (!data) {
        setStatus({ phase: 'no_account' });
      } else if (data.wallet_tos_accepted_at) {
        setStatus({ phase: 'accepted' });
      } else {
        setStatus({ phase: 'pending', accountId: data.id });
      }
    })();
  }, [parentId]);

  const handleAccept = async () => {
    if (status.phase !== 'pending') return;
    setIsAccepting(true);
    setAcceptError(null);
    try {
      const { error } = await supabase.rpc('accept_wallet_tos', {
        p_account_id: status.accountId,
      });
      if (error) throw new Error(error.message);
      setStatus({ phase: 'accepted' });
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to record acceptance. Please try again.');
    } finally {
      setIsAccepting(false);
    }
  };

  if (status.phase === 'loading') {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (status.phase === 'no_account') {
    return null;
  }

  if (status.phase === 'accepted') {
    return <>{children}</>;
  }

  // phase === 'pending' — show the ToS gate
  const items = [
    {
      icon: <Gift className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />,
      title: 'Bonus Credit Stays on Your Account',
      body: 'Any bonuses or promotional credits the school adds can only be used for BHCS invoices. They can\'t be withdrawn or transferred.',
    },
    {
      icon: <ArrowDownCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />,
      title: 'You Can Withdraw the Money You Deposit',
      body: 'If you ever want your deposited funds back, you can request a withdrawal. Requests are reviewed by school staff, typically within a few business days.',
    },
    {
      icon: <RefreshCcw className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />,
      title: 'Cancellation Refunds Go Back to BHCS Cash',
      body: 'If you cancel an enrollment that was paid with BHCS Cash, the refund is returned to your BHCS Cash balance rather than to your original payment method.',
    },
    {
      icon: <HeartHandshake className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />,
      title: 'Your Deposited Funds Are Always Protected',
      body: 'If you ever decide to close your account, any money you deposited will be refunded back to you. School-granted bonus credit would not carry over, but your own funds are always safe.',
    },
    {
      icon: <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />,
      title: 'BHCS Cash Is a Convenience Feature, Not a Bank',
      body: 'Think of it as a prepaid balance that makes paying tuition easier. It\'s not a financial product — it isn\'t FDIC-insured and doesn\'t earn interest.',
    },
  ];

  return (
    <div
      className="rounded-lg border border-blue-200 bg-blue-50 p-5 space-y-4"
      data-testid="wallet-tos-gate"
    >
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          A Quick Note Before You Continue
        </h3>
        <p className="text-sm text-gray-600">
          BHCS Cash is a prepaid balance you can use toward tuition and fees. Here's what you
          should know before getting started:
        </p>
      </div>

      <ul className="space-y-3">
        {items.map(({ icon, title, body }) => (
          <li key={title} className="flex gap-2.5">
            {icon}
            <span className="text-sm text-gray-700">
              <strong className="text-gray-900">{title}</strong>
              {' — '}
              {body}
            </span>
          </li>
        ))}
      </ul>

      <p className="text-sm text-gray-600">
        You can read the full details in{' '}
        <Link to="/terms-of-service" className="text-blue-700 underline hover:text-blue-900" target="_blank">
          our Terms of Service
        </Link>{' '}
        (Section 4, BHCS Cash).
      </p>

      {acceptError && (
        <p className="text-sm text-red-600" data-testid="wallet-tos-gate-error">
          {acceptError}
        </p>
      )}

      <button
        type="button"
        onClick={handleAccept}
        disabled={isAccepting}
        className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="wallet-tos-gate-accept"
      >
        {isAccepting ? 'Saving…' : 'I understand — Continue to BHCS Cash'}
      </button>
    </div>
  );
}
